<template>
    <CardLoader v-if="loading" />
    <div class="" v-else>
        <div class="d-flex justify-content-end">
            <MaterialButton variant="text" color="info" @click="viewPersons">
                View Persons
            </MaterialButton>
        </div>
        <div class="row g-3">
            <div class="col-md-6">
                <material-input id="workgroup-id" label="ID" v-model="workgroupData.id" disabled />
            </div>
            <div class="col-md-6">
                <material-input id="workgroup-name" label="Name" v-model="workgroupData.name" required :readOnly="readOnly" />
            </div>
            <div class="col-md-12">
                <material-input id="workgroup-description" label="Description" v-model="workgroupData.description" :readOnly="readOnly" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Person Count</label>
                    <HelpTextTooltip helpKey="activityinstanceworkgroup_detail_personcount" />
                </div>
                <material-input id="workgroup-person-count" v-model="workgroupData.personCount" type="number" :readOnly="readOnly" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Skill</label>
                    <HelpTextTooltip helpKey="activityinstanceworkgroup_detail_skill" />
                </div>
                <MaterialSelect v-model="workgroupData.skillId" :options="skillOptions" :disabled="readOnly" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Workgroup</label>
                    <HelpTextTooltip helpKey="activityinstanceworkgroup_detail_workgroup" />
                </div>
                <MaterialSelect v-model="workgroupData.workgroupId" :options="workgroupOptions" :disabled="readOnly" />
            </div>
            <div class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Sign Up Only</label>
                    <HelpTextTooltip helpKey="activityinstanceworkgroup_detail_signuponly" />
                </div>
                <MaterialSelect v-model="workgroupData.signUpOnly" :options="signUpOptions" :disabled="props.readOnly && !props.isWorkgroupAdmin" />
            </div>
            <div v-if="workgroupData.can_add_teams" class="col-md-6">
                <div class="d-flex align-items-center gap-2 mb-2">
                    <label class="form-label mb-0">Team</label>
                    <HelpTextTooltip helpKey="activityinstanceworkgroup_detail_team" />
                </div>
                <MaterialSelect v-model="workgroupData.teamId" :options="teamOptions" :disabled="readOnly" />
            </div>
        </div>

        <!-- Status Section -->
        <div class="mt-4">
            <h6 class="mb-3">Status: <span class="text-success">{{ workgroupData.status }}</span></h6>
        </div>

        <!-- Scheduling Options -->
        <div class="mt-4">
            <div class="d-flex align-items-center gap-2 mb-3">
                <h6 class="mb-0">Scheduling Options</h6>
                <HelpTextTooltip helpKey="activityinstanceworkgroup_detail_schedoptions" />
            </div>
            <div class="row g-3">
                <div class="col-md-6">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Convert-to-SignUp</span>
                        <span :class="workgroupData.convertToSignUp ? 'text-success' : 'text-danger'">
                            {{ workgroupData.convertToSignUp ? 'Enabled' : 'Disabled' }}
                        </span>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Auto-Scheduling</span>
                        <span :class="workgroupData.autoScheduling ? 'text-success' : 'text-danger'">
                            {{ workgroupData.autoScheduling ? 'Enabled' : 'Disabled' }}
                        </span>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Auto-Substitution</span>
                        <span :class="workgroupData.autoSubstitution ? 'text-success' : 'text-danger'">
                            {{ workgroupData.autoSubstitution ? 'Enabled' : 'Disabled' }}
                        </span>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Assignment Swap</span>
                        <span :class="workgroupData.assignmentSwap ? 'text-success' : 'text-danger'">
                            {{ workgroupData.assignmentSwap ? 'Enabled' : 'Disabled' }}
                        </span>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Allow Overlap</span>
                        <span :class="workgroupData.allowOverlap ? 'text-success' : 'text-danger'">
                            {{ workgroupData.allowOverlap ? 'Enabled' : 'Disabled' }}
                        </span>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Send SMS</span>
                        <span :class="workgroupData.sendSms ? 'text-success' : 'text-danger'">
                            {{ workgroupData.sendSms ? 'Enabled' : 'Disabled' }}
                        </span>
                    </div>
                </div>
                <div class="col-md-12">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Send secondary SMS alert</span>
                        <span :class="workgroupData.sendSecondarySms ? 'text-success' : 'text-danger'">
                            {{ workgroupData.sendSecondarySms ? 'Enabled' : 'Disabled' }}
                        </span>
                        <span class="ms-2 text-muted">[ ]</span>
                    </div>
                </div>
                <div class="col-md-12">
                    <p class="text-muted small">
                        Only send sms/text reminders when the assignment status is PENDING or has not yet been confirmed
                    </p>
                </div>
                <div class="col-md-12">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Priority (1=low, 100=high):</span>
                        <span class="fw-bold">{{ workgroupData.priority }}</span>
                    </div>
                </div>
                <div class="col-md-12">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Disable assignment decline option</span>
                        <span class="fw-bold">{{ workgroupData.disableDecline }}</span>
                        <span class="ms-2 text-muted">before activity date</span>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Auto Decline</span>
                        <span :class="workgroupData.autoDecline ? 'text-success' : 'text-danger'">
                            {{ workgroupData.autoDecline ? 'Enabled' : 'Disabled' }}
                        </span>
                    </div>
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center">
                        <span class="me-2">Advertise SignUp</span>
                        <span :class="workgroupData.advertiseSignup ? 'text-success' : 'text-danger'">
                            {{ workgroupData.advertiseSignup ? 'Enabled' : 'Disabled' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Report Dates -->
        <div class="mt-4">
            <div class="row g-3">
                <div class="col-md-6">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Last Admin Report Date</label>
                        <HelpTextTooltip helpKey="activityinstanceworkgroup_detail_adminreportdate" />
                    </div>
                    <material-input id="workgroup-last-admin-report-date" v-model="workgroupData.lastAdminReportDate"
                        disabled />
                </div>
                <div class="col-md-6">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Last Who-Is-On Report Date</label>
                        <HelpTextTooltip helpKey="activityinstanceworkgroup_detail_userreportdate" />
                    </div>
                    <material-input id="workgroup-last-who-is-on-report-date" v-model="workgroupData.lastWhoIsOnReportDate"
                        disabled />
                </div>
            </div>
        </div>

        <!-- Action Buttons -->
        <div v-if="!props.readOnly || props.isWorkgroupAdmin" class="d-flex justify-content-start mt-4 gap-2">
            <MaterialButton @click="saveWorkgroup" variant="primary" :class="['px-4', hasChanges && !isSaving ? 'pulse-glow' : '']" :disabled="isSaving">
                <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                    aria-hidden="true"></span>
                <i v-else class="material-icons text-lg me-1">save</i>
                {{ isSaving ? 'Saving...' : 'Save' }}
            </MaterialButton>
        </div>

        <!-- Persons Modal -->
        <ContentModal v-model="showPersonsModal" title="Persons with Selected Skill" size="xl">
            <BaseTable 
                :rows="personsWithSkill" 
                :columns="skillPersonsColumns" 
                :selectable="false" 
                :searchable="true"
                :loading="personsLoading" 
                :pagination="false" 
            />
        </ContentModal>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref, computed, watch } from "vue";
import { useToast } from "vue-toast-notification";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import MaterialInput from "@/components/common/MaterialInput.vue";
import MaterialButton from "@/components/common/MaterialButton.vue";
import MaterialSelect from "@/components/common/MaterialSelect.vue";
import HelpTextTooltip from "@/components/common/HelpTextTooltip.vue";
import CardLoader from "../../../components/Loader/CardLoader.vue";
import BaseTable from '@/components/Table/Table.vue';
import ContentModal from '../../../components/Modal/ContentModal.vue';

const props = defineProps({
    readOnly: { type: Boolean, default: false },
    isWorkgroupAdmin: { type: Boolean, default: false },
});

const toast = useToast();
const route = useRoute();
const store = useStore();

const locationId = route.params.locationid;
const instanceWorkgroupId = route.params.instanceworkgroupid;

const loading = computed(() => store.getters.instanceWorkgroupLoading);
const isSaving = ref(false);
const baseline = ref(null);

const hasChanges = computed(() => {
    if (!baseline.value) return false;
    return JSON.stringify(baseline.value) !== JSON.stringify({ ...workgroupData });
});
const activityId = ref(null);
const initialWorkgroupId = ref(null);

// Persons modal state
const showPersonsModal = ref(false);
const personsLoading = ref(false);
const personsWithSkill = computed(() => {
    const skillPersons = store.getters.skillPersonsForWorkgroup || [];
    return skillPersons.map((person) => ({
        personid: person.personid,
        fullname: `${person.firstname} ${person.lastname}`,
        email: person.email,
        skilllevel: person.skilllevel || '-',
    }));
});

const skillPersonsColumns = [
    { key: "personid", label: "ID" },
    { key: "fullname", label: "Name" },
    { key: "email", label: "Email" },
    { key: "skilllevel", label: "Skill Level" },
];

const workgroupData = reactive({
    id: "",
    name: "",
    description: "",
    personCount: 0,
    skillId: "",
    workgroupId: "",
    can_add_teams: false,
    teamId: "",
    signUpOnly: false,
    status: "Active",
    convertToSignUp: true,
    autoScheduling: true,
    autoSubstitution: true,
    assignmentSwap: true,
    allowOverlap: false,
    sendSms: true,
    sendSecondarySms: false,
    priority: 5,
    disableDecline: "1 day(s)",
    autoDecline: false,
    advertiseSignup: true,
    lastAdminReportDate: "",
    lastWhoIsOnReportDate: "",
});

const skillOptions = ref([]);
const teamOptions = ref([]);

// Computed property for workgroup options from store
const workgroupOptions = computed(() => {
    const workgroups = store.getters.allWorkgroups || [];
    return workgroups.map((workgroup) => ({
        value: String(workgroup.id),
        label: workgroup.workgroupName,
    }));
});

const signUpOptions = [
    { value: false, label: "No" },
    { value: true, label: "Yes" },
];

// Load instance workgroup details
const loadInstanceWorkgroupDetails = async () => {
    try {
        const detail = await store.dispatch("getInstanceWorkgroupDetails", instanceWorkgroupId);
        
        if (detail) {
            // Extract activity ID from detail
            activityId.value = detail.activity_id || detail.activityId || null;
            
            workgroupData.id = detail.id || "";
            workgroupData.name = detail.name || "";
            workgroupData.description = detail.description || "";
            workgroupData.personCount = detail.person_count || 0;
            workgroupData.skillId = detail.skill_id ? String(detail.skill_id) : "";
            // Store initial workgroup_id to set after workgroups are loaded
            if (detail.workgroup_id) {
                initialWorkgroupId.value = String(detail.workgroup_id);
                workgroupData.workgroupId = initialWorkgroupId.value;
            } else {
                workgroupData.workgroupId = "";
                initialWorkgroupId.value = null;
            }
            workgroupData.can_add_teams = detail.can_add_teams;
            workgroupData.teamId = detail.team_id ? String(detail.team_id) : "";
            workgroupData.signUpOnly = detail.sign_up_only || false;
            
            // Map scheduling options from nested object
            const schedulingOptions = detail.scheduling_options || {};
            workgroupData.status = schedulingOptions.status || "Active";
            workgroupData.convertToSignUp = schedulingOptions.convert_to_signup === "Enabled";
            workgroupData.autoScheduling = schedulingOptions.auto_scheduling === "Enabled";
            workgroupData.autoSubstitution = schedulingOptions.auto_substitution === "Enabled";
            workgroupData.assignmentSwap = schedulingOptions.assignment_swap === "Enabled";
            workgroupData.allowOverlap = schedulingOptions.allow_overlap === "Enabled";
            workgroupData.sendSms = schedulingOptions.send_sms === "Enabled";
            workgroupData.sendSecondarySms = schedulingOptions.send_secondary_sms_alert === "Enabled";
            workgroupData.priority = schedulingOptions.priority || 5;
            workgroupData.disableDecline = schedulingOptions.decline_days ? `${schedulingOptions.decline_days} day(s)` : "";
            workgroupData.autoDecline = schedulingOptions.auto_decline === "Enabled";
            workgroupData.advertiseSignup = schedulingOptions.advertise_signup === "Enabled";
            
            // Map report dates
            workgroupData.lastAdminReportDate = detail.last_admin_report_date || "";
            workgroupData.lastWhoIsOnReportDate = detail.last_who_is_on_report_date || "";
            baseline.value = { ...workgroupData };
        }
    } catch (error) {
        console.error("Failed to load instance workgroup details:", error);
        toast.error("Failed to load instance workgroup details", { position: "top-right" });
    }
};

// Load skills for location
const loadLocationSkills = async () => {
    try {
        const skills = await store.dispatch("getLocationSkills", {
            locationId,
            all: true,
        });

        // Skills API returns a direct array
        const skillsList = Array.isArray(skills) ? skills : [];
        
        skillOptions.value = skillsList.map((skill) => ({
            value: String(skill.skillid),
            label: skill.skillname || "",
        }));
    } catch (error) {
        console.error("Failed to load location skills:", error);
        toast.error("Failed to load skills", { position: "top-right" });
    }
};

// Load teams for instance workgroup
const loadInstanceWorkgroupTeams = async () => {
    try {
        const response = await store.dispatch("getInstanceWorkgroupTeams", instanceWorkgroupId);

        // Teams API returns { teams: [...] }
        const teamsList = response?.teams || [];
        
        teamOptions.value = teamsList.map((team) => ({
            value: String(team.id),
            label: team.name || "",
        }));
    } catch (error) {
        console.error("Failed to load instance workgroup teams:", error);
        toast.error("Failed to load teams", { position: "top-right" });
    }
};

async function saveWorkgroup() {
    isSaving.value = true;
    try {
        await store.dispatch("updateInstanceWorkgroupDetails", {
            instanceworkgroupid: instanceWorkgroupId,
            name: workgroupData.name,
            description: workgroupData.description,
            person_count: workgroupData.personCount,
            sign_up_only: workgroupData.signUpOnly,
            skill_id: workgroupData.skillId ? Number(workgroupData.skillId) : null,
            workgroup_id: workgroupData.workgroupId ? Number(workgroupData.workgroupId) : null,
            team_id: workgroupData.teamId ? Number(workgroupData.teamId) : null,
        });

        baseline.value = { ...workgroupData };
        toast.success("Workgroup details saved successfully", { position: "top-right" });
        
        // Reload data to get updated values
        await loadInstanceWorkgroupDetails();
    } catch (error) {
        console.error("Failed to save workgroup details:", error);
        toast.error("Failed to save workgroup details", { position: "top-right" });
    } finally {
        isSaving.value = false;
    }
}

const viewPersons = async () => {
    if (!workgroupData.skillId) {
        toast.warning("Please select a skill first!", { position: "top-right" });
        return;
    }

    showPersonsModal.value = true;
    personsLoading.value = true;

    try {
        await store.dispatch("getSkillPersonsForWorkgroup", {
            skillId: Number(workgroupData.skillId),
            locationId: Number(locationId),
        });
    } catch (error) {
        console.error("Error fetching skill persons:", error);
        toast.error("Failed to load persons for selected skill.", {
            position: "top-right",
        });
    } finally {
        personsLoading.value = false;
    }
};

// Load workgroups for activity
const loadWorkgroups = async () => {
    if (!activityId.value) {
        return;
    }
    
    try {
        await store.dispatch("fetchWorkgroups", {
            activityId: activityId.value,
            all: true,
        });
        
        // After workgroups are loaded, ensure the workgroupId is selected if it was set
        if (initialWorkgroupId.value && workgroupOptions.value.length > 0) {
            // Verify the workgroup exists in the options
            const workgroupExists = workgroupOptions.value.some(
                option => option.value === initialWorkgroupId.value
            );
            if (workgroupExists) {
                workgroupData.workgroupId = initialWorkgroupId.value;
            }
        }
    } catch (error) {
        console.error("Failed to fetch workgroups:", error);
        toast.error("Failed to fetch workgroups", { position: "top-right" });
    }
};

// Watch for workgroup options to become available and ensure selection
watch(workgroupOptions, (options) => {
    if (options.length > 0 && initialWorkgroupId.value) {
        // Check if the initial workgroup_id exists in the options
        const workgroupExists = options.some(
            option => option.value === initialWorkgroupId.value
        );
        if (workgroupExists && workgroupData.workgroupId !== initialWorkgroupId.value) {
            workgroupData.workgroupId = initialWorkgroupId.value;
        }
    }
});

onMounted(async () => {
    await loadInstanceWorkgroupDetails();
    
    if (activityId.value) {
        await loadWorkgroups();
    }
    
    await loadLocationSkills();
    
    if (workgroupData.can_add_teams) {
        await loadInstanceWorkgroupTeams();
    }
});
</script>

<style scoped>
.form-label {
    color: #7b809a;
}

.d-flex {
    display: flex;
}

.align-items-center {
    align-items: center;
}

.gap-2 {
    gap: 0.5rem;
}
</style>